import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import {
	ApiClient,
	type EvaluationType,
	type FetchProjectAssignabilityResponse,
	type HttpErrorResult,
	type NewClassType,
	type KycDetails,
	type KycProfile,
	type StudentClassImportResponse,
	type PlanDetails,
	type TemporaryData,
	type ExternalAccountProvider,
	type ClassGrade,
	type QweebiClassStatus,
	type SpecifiedClassGrade,
	type UserCreditUsage,
	type CoTeacherInvitation,
} from '@/stores/api/apiClient';
import type { AxiosError } from 'axios';
import { useAuthStore, type UserCredentials } from '@/stores/auth';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/vue';
import type { AddStudentRecord } from '@/helpers';

enum UserType {
	Teacher = 'TEACHER',
	Student = 'STUDENT',
}

enum LoginError {
	InvalidUsernamePasswordCombination,
	SocialOnlyAccount,
	OtherError,
}

export enum SignUpError {
	ExistingUser = 'SIGNUP_EXISTING_USER',
	InvalidPassword = 'SIGNUP_INVALID_PASSWORD',
	PasswordConfirmationMismatch = 'SIGNUP_PASSWORD_CONFIRMATION_INVALID',
	InvalidPromoCode = 'SIGNUP_INVALID_PROMO_CODE',
	InvalidEmail = 'SIGNUP_INVALID_EMAIL',
	OtherError = 'OTHER_ERROR',
	SocialAccountExists = 'SIGNUP_SOCIAL_ACCOUNT_EXISTS',
}

/**
 * A linked external account (e.g., Google) for a teacher.
 *
 * Used for:
 * - Social login/signup ("Continue with Google/Microsoft")
 * - Google Classroom imports (class and student sync)
 *
 * The `classroomLinked` flag indicates if Google Classroom is currently connected.
 * Only accounts with classroomLinked=true should show the "Unlink Classroom" option.
 *
 * The `usedForLogin` flag indicates if this account has been used for social login.
 */
export type LinkedAccount = {
	id: string;
	provider: ExternalAccountProvider;
	/** Email or identifier from the external provider */
	humanIdentifier: string;
	/** True if this linked account has Google Classroom connected (for imports) */
	classroomLinked: boolean;
	/** True if this linked account has been used for social login */
	usedForLogin: boolean;
};

export type EmailVerificationStatus = 'NOT_VERIFIED' | 'LEGACY_VERIFIED' | 'VERIFIED';
export type EmailVerificationTokenInfo = {
	generatedAt: string;
	validity: string;
	expiresAt: string;
};

/* eslint-disable @typescript-eslint/no-redundant-type-constituents -- literals document known values */
export type SignUpSource =
	| 'project_roller_coaster'
	| 'project_rube_goldberg'
	| 'referral_teacherinvite'
	| 'referral_coteacherinvite'
	| string
	| null;
/* eslint-enable @typescript-eslint/no-redundant-type-constituents */

/**
 * The method used for initial signup.
 * - EMAIL: Traditional email/password signup
 * - GOOGLE: Signed up via "Continue with Google"
 * - MICROSOFT: Signed up via "Continue with Microsoft"
 */
export type SignupMethod = 'EMAIL' | 'GOOGLE' | 'MICROSOFT';
export const DASHBOARD_CREDENTIALS_COOKIE_NAME = 'credentials';
export const MAKERSPACE_CREDENTIALS_COOKIE_NAME = 'credentials_local';
export type CredentialsCookieName =
	typeof DASHBOARD_CREDENTIALS_COOKIE_NAME | typeof MAKERSPACE_CREDENTIALS_COOKIE_NAME;

type UserDetails = {
	id: string;
	email: string;
	type: UserType;
	name: string;
	timestamps?: {
		createdAt: string;
	};
	profile: KycProfile | null;
	dashboardTemporaryData: TemporaryData | null;
	plan: PlanDetails | null;
	linkedAccounts: LinkedAccount[];
	signUpSource: SignUpSource;
	emailVerificationStatus: EmailVerificationStatus;
	emailVerificationDetails?: EmailVerificationTokenInfo;
	/** The method used for initial signup (EMAIL, GOOGLE, or MICROSOFT) */
	signupMethod: SignupMethod;
};

type UserState = {
	user: UserDetails | null;
	classes: DetailedClassInfo[] | null;
	coTeachingClasses: DetailedClassInfo[];
	pendingCoTeacherInvitations: CoTeacherInvitation[];
	currentClass: DetailedClassInfo | null;
	studentsInClass: StudentInfo[];
	categories: CategoryTypeV2[];
};

const getCredentialsCookieDomain = () => {
	const currentDomain = window.location.hostname;
	if (/qweebi\.com$/i.test(currentDomain)) {
		return 'qweebi.com';
	}
	return currentDomain;
};

export type Timestamps = {
	createdAt: string;
	updatedAt: string;
};

type DetailedClassInfo =
	| {
			id: string;
			userId: string;
			classCode: string;
			name: string;
			timestamps: Timestamps;
			numberOfStudents: number;
			numberOfActiveStudents?: number;
			grade: ClassGrade;
			status: QweebiClassStatus;
			source: 'INTERNAL';
			externalInfo: null;
			invitedAt: string | null;
			acceptedAt: string | null;
	  }
	| {
			id: string;
			userId: string;
			classCode: string;
			name: string;
			timestamps: Timestamps;
			numberOfStudents: number;
			numberOfActiveStudents?: number;
			grade: ClassGrade;
			status: QweebiClassStatus;
			source: 'EXTERNAL';
			externalInfo: {
				externalId: string;
				externalOwnerId: string;
				provider: ExternalAccountProvider;
			};
			invitedAt: string | null;
			acceptedAt: string | null;
	  };

type MyClassesResponse = {
	classes: DetailedClassInfo[];
	// Optional: older API deployments may not return this field yet.
	coTeachingClasses?: DetailedClassInfo[];
};

type ClassAssignment = {
	classId: string;
	projectId: string;
	name: string;
	numberOfStudents: number;
	assignment: {
		id: string;
		timestamps: Timestamps;
		uniqueUnitsWithPermissions?: {
			unitId: string;
			hasPermissionToRun: boolean;
		}[];
	};
};

type ProjectWithClassesRes = {
	id: string;
	name: string;
	lessonCount: number;
	classAssignments: ClassAssignment[];
	userCreditUsage: UserCreditUsage;
};

export type UniqueUnitWithPermission = Omit<CategoryUnit, 'ordering'> & { hasPermissionToRun: boolean };

export type ClassAssignmentResponseItem = {
	assignmentId: string;
	timestamps: Timestamps;
	category: CategoryTypeV2;
	uniqueUnits: UniqueUnitWithPermission[];
};

export type ClassAssignmentsResponse = {
	assignments: ClassAssignmentResponseItem[];
};

type StudentInfo = {
	id: string;
	name: string;
	loginCode: string;
	timestamps: Timestamps;
	externalInfo?: {
		id: string;
		platform: ExternalAccountProvider;
		status: 'ACTIVE' | 'INACTIVE';
	};
};

type SkillsType = {
	core: {
		ratingMethod: 'POINT_BASED_101';
		skill: {
			description: string;
			id: string;
			name: string;
			status: 'ACTIVE' | 'INACTIVE';
			timestamps: Timestamps;
		};
	}[];
	additional: {
		ratingMethod: 'POINT_BASED_101';
		skill: {
			description: string;
			id: string;
			name: string;
			status: 'ACTIVE' | 'INACTIVE';
			timestamps: Timestamps;
		};
	}[];
};

type AssessmentRubricType = {
	id: string;
	skills: SkillsType;
	classId: string;
	lessonId: string;
	rubricId: string;
	timestamps: Timestamps;
	userId: string;
};

type ProjectInfo = {
	id: string;
	name: string;
	timestamps: Timestamps;
	assessmentRubric: AssessmentRubricType;
	challengeLevel: 'FINAL' | 'MINI';
};

type LessonInfoV2 = {
	id: string;
	name: string;
	timestamps: Timestamps;
	assessmentRubric: AssessmentRubricType;
	challengeLevel: 'FINAL' | 'MINI';
	description: string;
	thumbnailUrl: string;
	resourceFormat: 'PACKAGED' | 'UNITY_CCD';
	secondaryScoreSortDirection: 'LOWEST' | 'HIGHEST' | null;
	orderingInUnit: number;
	teacherResources: {
		lessonPlan: {
			link: string;
		};
		studentHandouts: {
			link: string;
		};
		teacherSlides: {
			link: string;
		};
		additionalResources: {
			title: string;
			link: string;
		}[];
	};
};

type ShowcaseInfo = {
	id: string;
	name: string;
	subtitle: string;
	attribution: string;
	timestamps: Timestamps;
	accessMode: 'PUBLIC' | 'PRIVATE';
	user: {
		id: string;
		name: string;
		timestamps: Timestamps;
	};
	description: string;
	submissions: Submission[];
};

type PublicShowcaseSubmission = {
	id: string;
	video: Video;
	userInfo: {
		name: string;
	};
};

type PublicShowcaseInfo = {
	id: string;
	name: string;
	description: string;
	subtitle: string;
	attribution: string;
	user: {
		name: string;
	};
	submissions: PublicShowcaseSubmission[];
};

type ChangeShowcaseMode = {
	id: string;
	accessMode: 'PUBLIC' | 'PRIVATE';
};

type CategoriesType = {
	id: string;
	name: string;
	timestamps?: Timestamps;
};

enum ProjectPricingTier {
	Free = 'FREE',
	Paid = 'PAID',
}

export type ProjectPrepTime = 'LOW' | 'MEDIUM' | 'HIGH';
export type ProjectStatus = 'UPCOMING' | 'ACTIVE' | 'INACTIVE';

export type CategoryUnitDetails = {
	name: string;
	thumbnail: string;
	slideUrl: string;
};
export type CategoryUnit = {
	id: string;
	details: CategoryUnitDetails;
	timestamps: Timestamps;
	challenges: LessonInfoV2[];
	ordering: number;
};

export type CategoryLessonsResponse = {
	lessons: CategoryLesson[];
};
export type CategoryLesson = {
	id: string;
	categoryId: string;
	ordering: number;
	details: LessonDetails;
	units: CategoryUnit[];
	timestamps: Timestamps;
};

export type CategoryTypeV2 = {
	id: string;
	name: string;
	timestamps?: Timestamps;
	description: string;
	duration: string;
	platformSupport: string;
	prepTime: ProjectPrepTime;
	status: ProjectStatus;
	suggestedGrades: {
		minimum: number;
		maximum: number;
	};
	thumbnailUrl: string;
	pricingTier: ProjectPricingTier;
	lessons: CategoryLesson[];
};

type CategoryOverview = {
	imageGallery: {
		id: string;
		url: string;
	}[];
	detailedDescription: string;
	sections: {
		order: string;
		title: string;
		content: string;
	}[];
};

export type ChallengeDetails = {
	name: string;
	destination: string;
};
export type LessonDetails = {
	slug: string;
	index: number;
	title: string;
	overviewDescription: string;
	prepDescription: string;
	prepTime: string;
	duration: string;
	thumbnailUrl: string;
	lessonSlides: string;
	overview: string;
	learningObjectives: string[];
	outline: {
		title: string;
		duration: string;
		description: string;
	}[];
	material: {
		teacherSlides: string;
		studentJournals?: string;
		liveSessionRecording?: string;
	};
	challenges: ChallengeDetails[];
};
export type ReviewDetails = {
	rating: number;
	contents: string;
	reviewerName: string;
	reviewerRole: string;
};
export type SubjectStandard = {
	title: string;
	standards: string[];
};
export type ProjectDetails = {
	id: string;
	name: string;
	suggestedGrades: {
		minimum: SpecifiedClassGrade;
		maximum: SpecifiedClassGrade;
	};
	prepTime: ProjectPrepTime;
	duration: string;
	previewImages: string[];
	summary: string;
	shortDescription: string;
	leaderboardDescription: string;
	primaryScoreHeading?: string;
	secondaryScoreHeading?: string;
	primaryScoreUnit?: string;
	secondaryScoreUnit?: string;
	studentLearnings: string[];
	topics: string;
	realLifeCareer: string;
	subjectStandards: string[];
	segregatedSubjectStandards: SubjectStandard[];
	teacherResources: {
		teacherSlides: string;
		studentJournals?: string;
		liveSessionRecording?: string;
	};
	lessons: CategoryLesson[];
	userReview: {
		overallRating: number;
		totalNumberOfReviews: number;
		featuredReviews: ReviewDetails[];
	} | null;
	teachingGuide: string;
};

export type ProjectDetailsProvider = {
	currentProjectDetails: () => ProjectDetails;
};

type CategoryDetails = {
	overview: CategoryOverview;
};

export type CategoryMetadata = {
	name: string;
	topics: string;
	summary: string;
	duration: string;
	prepTime: ProjectPrepTime;
	userReview: {
		overallRating: number;
		featuredReviews: ReviewDetails[];
		totalNumberOfReviews: number;
	} | null;
	previewImages: string[];
	teachingGuide: string;
	realLifeCareer: string;
	suggestedGrades: {
		maximum: SpecifiedClassGrade;
		minimum: SpecifiedClassGrade;
	};
	shortDescription: string;
	studentLearnings: string[];
	subjectStandards: string[];
	teacherResources: {
		teacherSlides: string;
		studentJournals?: string;
		liveSessionRecording?: string;
	};
	leaderboardDescription: string;
	primaryScoreHeading?: string;
	secondaryScoreHeading?: string;
	primaryScoreUnit?: string;
	primaryScoreUnitHeading?: string;
	secondaryScoreUnit?: string;
	secondaryScoreUnitHeading?: string;
	swapLeaderboardScores?: boolean;
	scheduledData?: {
		month: string;
		description?: string;
		description35?: string;
		description68?: string;
		thumbnailSquare: string;
	};
	themes?: string[];
};

export type CategoryMetadataResponse = {
	categoryId: string;
	metadata: CategoryMetadata;
	timestamps: Timestamps;
};

export type CategoryMetadataResponseMultiple = {
	categories: CategoryMetadataResponse[];
};

export type CategoryCurrentAssignmentStatus = {
	assignedClassCount: number;
	latestAssignedAt: string | null;
};

export type CategoryQueryResult = {
	id: string;
	name: string;
	description: string;
	thumbnailUrl: string;
	suggestedGrades: {
		minimum: number;
		maximum: number;
	};
	prepTime: ProjectPrepTime;
	status: ProjectStatus;
	duration: string;
	pricingTier: ProjectPricingTier;
	timestamps: Timestamps;
	metadata: CategoryMetadata;
	assignability: FetchProjectAssignabilityResponse;
	currentAssignmentStatus: CategoryCurrentAssignmentStatus | null;
};

type Video = {
	id: string;
	userId: string;
	playback: {
		hls: string;
		dash: string;
		share: string;
	};
	createdAt: string;
	projectId: string;
	thumbnail: string;
	title: string;
};

type EvaluationResultType = {
	criteria: {
		id: string;
		userId: string;
		classId: string;
		lessonId: string;
		rubricId: string;
		timestamps: Timestamps;
		skills: SkillsType;
	};
	ratings: {
		rating: number;
		skillId: string;
	}[];
	score: {
		maxRating: string;
		normalized: string;
		totalRating: string;
	};
	graderId: string;
	id: string;
	submissionId: string;
	timestamps: Timestamps;
};

type CommentType = {
	id: string;
	submissionId: string;
	commentor: object;
	content: string;
	timestamps: Timestamps;
};

type Submission = {
	id: string;
	name: string;
	userId: string;
	lessonId: string;
	status: 'PENDING_EVALUATION' | 'RESUBMISSION_REQUESTED' | 'EVALUATED';
	video: Video;
	timestamps: Timestamps;
	comments: CommentType[];
	userInfo: {
		id: string;
		name: string;
	};
	evaluation?: EvaluationResultType;
};
type SubmissionInfo = {
	id: string;
	isFavorite?: boolean;
	student: {
		id: string;
		name: string;
		grade: string;
	};
	submission: Submission;
};

type StudentsInClassResponse = {
	students: StudentInfo[];
};

type ShowcasesResponse = {
	showcases: ShowcaseInfo[];
};

type SubmissionsResponse = {
	lessonId: string;
	classId: string;
	submissions: SubmissionInfo[];
};

type SuccessResponse = {
	code: number;
	status: string;
	message: string;
};

type TokenGrant = {
	accessToken: string;
	expiresAt: string;
};

class LoginResponse {
	user: UserDetails;
	token: TokenGrant;

	constructor(
		user: {
			id: string;
			email: string;
			type: UserType;
			name: string;
			profile: KycProfile | null;
			dashboardTemporaryData: TemporaryData | null;
			plan: PlanDetails | null;
			linkedAccounts: LinkedAccount[];
			signUpSource: SignUpSource;
			emailVerificationStatus: EmailVerificationStatus;
			signupMethod: SignupMethod;
		},
		token: {
			accessToken: string;
			expiresAt: string;
		},
	) {
		this.user = user;
		this.token = token;
	}
}

class LoginFailure {
	error: LoginError;
	message?: string;

	constructor(error: LoginError, message?: string) {
		this.error = error;
		this.message = message;
	}
}

type CheckPromoCodeResponse = {
	isPromoCodeValid: boolean;
};

export type SignUpResponse = {
	user: UserDetails;
	token: TokenGrant;
};

export type SignUpFailure = {
	error: SignUpError;
	message?: string;
};

// TODO: Move this to a Pinia plugin
let _apiClient: ApiClient | null = null;
export const apiClientProvider = () => {
	_apiClient ??= new ApiClient();

	return _apiClient;
};

type LeaderboardScore = {
	studentId: string;
	studentName: string;
	score: number | null;
	secondaryScore: number | null;
	rank: number | null;
};

type LeaderboardResponse = {
	classId: string;
	lessonId: string;
	scores: LeaderboardScore[];
};

const useUserStore = defineStore('user', {
	state: (): UserState => {
		return {
			user: null,
			classes: null,
			coTeachingClasses: [],
			pendingCoTeacherInvitations: [],
			currentClass: null,
			studentsInClass: [],
			categories: [],
		};
	},

	actions: {
		loginUser: async function (email: string, password: string): Promise<LoginResponse | LoginFailure> {
			try {
				const loginUserResponse = (await apiClientProvider().login(email, password)).data as LoginResponse;
				return loginUserResponse;
			} catch (error) {
				const axiosError = error as AxiosError;
				if (axiosError.response!.status === 400) {
					const responseData = axiosError.response!.data as {
						code: string;
						messages: string[];
					};

					let loginError: LoginError;
					let message: string | undefined;
					switch (responseData.code) {
						case 'LOGIN_INVALID_USERNAME_OR_PASSWORD': {
							loginError = LoginError.InvalidUsernamePasswordCombination;
							break;
						}
						case 'LOGIN_SOCIAL_ONLY_ACCOUNT': {
							loginError = LoginError.SocialOnlyAccount;
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
							message = responseData.messages?.[0];
							break;
						}
						default: {
							loginError = LoginError.OtherError;
						}
					}

					return new LoginFailure(loginError, message);
				} else {
					return new LoginFailure(LoginError.OtherError);
				}
			}
		},

		checkPromoCode: async function (promoCode: string): Promise<CheckPromoCodeResponse | 'CheckPromoCodeFailure'> {
			try {
				const checkPromoCodeResponse = (await apiClientProvider().checkPromoCode(promoCode))
					.data as CheckPromoCodeResponse;
				return checkPromoCodeResponse;
			} catch {
				return 'CheckPromoCodeFailure';
			}
		},

		signupUser: async function (
			email: string,
			password: string,
			name: string,
			promoCode: string,
			signUpSource: string | null,
			referralToken?: string,
		): Promise<SignUpResponse | SignUpFailure> {
			try {
				// We have removed password confirmation from our sign-up form but it still remains in the API endpoint
				const passwordConfirmation = password;
				const signupResponse = (
					await apiClientProvider().signUp(
						email,
						password,
						passwordConfirmation,
						name,
						promoCode,
						signUpSource ?? 'unknown',
						referralToken,
					)
				).data as SignUpResponse;
				return signupResponse;
			} catch (error) {
				const axiosError = error as AxiosError;

				if (axiosError.response!.status === 400) {
					const responseData = axiosError.response!.data as {
						code: string;
						messages: string[];
					};

					let signUpError: SignUpError;
					let message: string | undefined;
					switch (responseData.code) {
						case SignUpError.ExistingUser.toString(): {
							signUpError = SignUpError.ExistingUser;
							break;
						}
						case SignUpError.InvalidPassword.toString(): {
							signUpError = SignUpError.InvalidPassword;
							break;
						}
						case SignUpError.InvalidPromoCode.toString(): {
							signUpError = SignUpError.InvalidPromoCode;
							break;
						}
						case SignUpError.InvalidEmail.toString(): {
							signUpError = SignUpError.InvalidEmail;
							break;
						}
						case SignUpError.SocialAccountExists.toString(): {
							signUpError = SignUpError.SocialAccountExists;
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
							message = responseData.messages?.[0];
							break;
						}
						case SignUpError.PasswordConfirmationMismatch.toString(): {
							// This should not arise since we now ask user to enter their password only once
							signUpError = SignUpError.OtherError;
							break;
						}
						default: {
							signUpError = SignUpError.OtherError;
						}
					}

					return { error: signUpError, message: message };
				} else {
					return { error: SignUpError.OtherError };
				}
			}
		},

		requestPasswordReset: async function (email: string): Promise<unknown> {
			return (await apiClientProvider().requestPassword(email)).data as unknown;
		},

		resetPassword: async function (resetObj: {
			password: string;
			passwordConfirmation: string;
			token: string;
		}): Promise<unknown> {
			return (await apiClientProvider().resetPassword(resetObj)).data as unknown;
		},

		updateUserDetails: function (user: UserDetails) {
			this.user = user;
		},

		updateUser: function (
			user: UserDetails,
			credentials: UserCredentials,
			credentialsCookieName: CredentialsCookieName = DASHBOARD_CREDENTIALS_COOKIE_NAME,
		) {
			this.user = user;
			useAuthStore().updateCredentials(credentials);

			// Side-effect. What is the right way to handle this in Pinia?
			this.saveCredentialsCookie(credentials, credentialsCookieName);
		},

		clearCredentialsCookie: function (
			credentialsCookieName: CredentialsCookieName = DASHBOARD_CREDENTIALS_COOKIE_NAME,
		) {
			const cookieDomain = getCredentialsCookieDomain();

			document.cookie = `${credentialsCookieName}=; domain=${cookieDomain} ; expires=${new Date(0).toUTCString()} ; path=/`;
			Sentry.setUser(null);
		},

		saveCredentialsCookie: function (
			credentials: UserCredentials,
			credentialsCookieName: CredentialsCookieName = DASHBOARD_CREDENTIALS_COOKIE_NAME,
		) {
			const cookieDomain = getCredentialsCookieDomain();
			const cookieCredentials = encodeURIComponent(JSON.stringify(credentials));
			const cookieExpires = credentials.expiresAt.toJSDate().toUTCString();

			document.cookie = `${credentialsCookieName}=${cookieCredentials} ; domain=${cookieDomain} ; expires=${cookieExpires} ; path=/`;
		},

		readCredentialsCookie: function (
			credentialsCookieName: CredentialsCookieName = DASHBOARD_CREDENTIALS_COOKIE_NAME,
		): UserCredentials | null {
			const cookiePrefix = `${credentialsCookieName}=`;
			const credentialCookie = document.cookie
				.split(';')
				.map((cookie) => cookie.trim())
				.find((cookie: string) => cookie.startsWith(cookiePrefix))
				?.slice(cookiePrefix.length)
				.trim();

			if (!credentialCookie) {
				return null;
			}

			const credentials = JSON.parse(decodeURIComponent(credentialCookie)) as {
				accessToken: string;
				expiresAt: string;
			};
			return {
				accessToken: credentials.accessToken,
				expiresAt: DateTime.fromISO(credentials.expiresAt),
			};
		},

		logOut: async function () {
			this.clearCredentialsCookie();
			useAuthStore().updateCredentials(null);
			posthog.reset();
			await apiClientProvider().logout();
			window.Intercom('shutdown');
		},

		loadCredentials: function (credentialsCookieName: CredentialsCookieName = DASHBOARD_CREDENTIALS_COOKIE_NAME) {
			const credentialsFromCookie = this.readCredentialsCookie(credentialsCookieName);

			if (credentialsFromCookie !== null) {
				if (credentialsFromCookie.expiresAt.diffNow().as('seconds') > 0) {
					useAuthStore().updateCredentials(credentialsFromCookie);
				} else {
					this.clearCredentialsCookie(credentialsCookieName);
					useAuthStore().updateCredentials(null);
				}
			} else {
				useAuthStore().updateCredentials(null);
			}
		},

		refreshUserDetails: async function () {
			try {
				const userDetails = (await apiClientProvider().self()).data as UserDetails;
				this.user = userDetails;
				Sentry.setUser({
					id: this.user.id,
					email: this.user.email,
				});
			} catch (error) {
				console.error("Error refreshing user's details. Error:", error);
			}
		},

		fetchMyClasses: async function () {
			try {
				const myClasses = (await apiClientProvider().myClasses()).data as MyClassesResponse;
				const byCreatedAtDescending = (a: DetailedClassInfo, b: DetailedClassInfo): number => {
					const dateA = new Date(a.timestamps.createdAt).getTime();
					const dateB = new Date(b.timestamps.createdAt).getTime();

					// Descending order (newest to oldest):
					if (dateB < dateA) {
						return -1;
					} else if (dateB > dateA) {
						return 1;
					} else {
						return 0;
					}
				};
				this.classes = myClasses.classes.sort(byCreatedAtDescending);
				this.coTeachingClasses = (myClasses.coTeachingClasses ?? []).sort(byCreatedAtDescending);
			} catch (error) {
				console.error(error);
			}
		},

		fetchCoTeacherInvitations: async function () {
			const response = await apiClientProvider().coTeacherInvitations();
			if ('reason' in response) {
				this.pendingCoTeacherInvitations = [];
				return;
			}
			this.pendingCoTeacherInvitations = response.invitations;
		},

		acceptCoTeacherInvitation: async function (classId: string): Promise<boolean> {
			const response = await apiClientProvider().acceptCoTeacherInvite(classId);
			if ('reason' in response) {
				if (response.reason === 'NOT_FOUND') {
					await this.fetchCoTeacherInvitations();
				}
				return false;
			}
			this.pendingCoTeacherInvitations = this.pendingCoTeacherInvitations.filter(
				(invitation) => invitation.classId !== classId,
			);
			await this.fetchMyClasses();
			return true;
		},

		declineCoTeacherInvitation: async function (classId: string): Promise<boolean> {
			const response = await apiClientProvider().declineCoTeacherInvite(classId);
			if ('reason' in response) {
				if (response.reason === 'NOT_FOUND') {
					await this.fetchCoTeacherInvitations();
				}
				return false;
			}
			this.pendingCoTeacherInvitations = this.pendingCoTeacherInvitations.filter(
				(invitation) => invitation.classId !== classId,
			);
			return true;
		},

		fetchProjectWithClasses: async function (id: string) {
			try {
				const project = (await apiClientProvider().fetchClassesForProject(id)).data as ProjectWithClassesRes;
				return project;
			} catch (error) {
				console.error(error);
			}
		},

		/**
		 * Assigns a project to classes or updates unit permissions on existing assignments.
		 * - When `units` is not provided: Creates new assignments for the project in the specified classes.
		 * - When `units` is provided: Updates unit permissions (add/remove units) for existing assignments.
		 *
		 * @param projectId - The ID of the project to assign
		 * @param classIds - Array of class IDs to assign the project to
		 * @param units - Optional. When provided, updates unit permissions instead of creating new assignments.
		 *                Should contain `unitIdsToAdd` and `unitIdsToRemove` arrays.
		 * @returns The updated project with class assignments information
		 */
		createOrUpdateAssignment: async function (
			projectId: string,
			classIds: string[],
			units?: { unitIdsToAdd: string[]; unitIdsToRemove: string[] },
		): Promise<ProjectWithClassesRes> {
			const project = (
				await apiClientProvider().createAssignment({
					projectId: projectId,
					classes: classIds.map((id) => {
						return { id: id };
					}),
					units: units,
				})
			).data as ProjectWithClassesRes;
			return project;
		},

		unassignProjectFromClass: async function (assignmentId: string): Promise<number> {
			return ((await apiClientProvider().unassignProjectFromClass(assignmentId)).data as SuccessResponse).code;
		},

		createClasses: async function (
			classesToAdd: { className: string; classGrade: ClassGrade }[],
		): Promise<DetailedClassInfo[] | HttpErrorResult> {
			const apiClient = apiClientProvider();

			const classesSuccessfullyAdded: DetailedClassInfo[] = [];
			for (const c of classesToAdd) {
				const response = await apiClient.createClass(c.className, c.classGrade);

				if ('errorModel' in response) {
					return response;
				} else {
					classesSuccessfullyAdded.push(response);
				}
			}

			await this.fetchMyClasses();

			return classesSuccessfullyAdded;
		},

		createClass: async function (
			newClassName: string,
			grade: ClassGrade,
		): Promise<DetailedClassInfo | HttpErrorResult> {
			const apiClient = apiClientProvider();

			const response = await apiClient.createClass(newClassName, grade);

			if ('errorModel' in response) {
				return response;
			} else {
				const newClassAdded = response;
				await this.fetchMyClasses();
				return newClassAdded;
			}
		},

		updateClass: async function (newClass: NewClassType): Promise<DetailedClassInfo> {
			return (await apiClientProvider().updateClass(newClass)).data as DetailedClassInfo;
		},

		submitKyc: async function (newClass: KycDetails) {
			try {
				return (await apiClientProvider().submitKyc(newClass)).data as KycDetails;
			} catch (error) {
				console.error(error);
			}
		},

		updateTemporaryData: async function (data: TemporaryData) {
			try {
				const updatedDetails = (await apiClientProvider().updateTemporaryData(data)).data as TemporaryData;
				await this.refreshUserDetails();
				return updatedDetails;
			} catch (error) {
				console.error(error);
			}
		},

		toggleClassStatus: async function (newClass: NewClassType): Promise<DetailedClassInfo> {
			const result = (await apiClientProvider().updateClass(newClass)).data as DetailedClassInfo;
			await this.refreshUserDetails();
			this.currentClass = result;
			return result;
		},

		setCurrentClass: function (classDetails: DetailedClassInfo): DetailedClassInfo {
			if (this.classes !== null) {
				const indexOfItemToUpdate = this.classes.findIndex((c) => c.id === classDetails.id);
				if (indexOfItemToUpdate >= 0) {
					this.classes[indexOfItemToUpdate] = classDetails;
				}
			}

			this.currentClass = classDetails;
			return this.currentClass;
		},

		updateCurrentClassFromId: async function (classId: string): Promise<DetailedClassInfo | null> {
			if (this.classes === null) {
				// Can happen if the user directly links to a specific class. We should figure out a better way.
				await this.fetchMyClasses();
			}

			const allClasses = [...(this.classes ?? []), ...this.coTeachingClasses];
			const classToSet = allClasses.find((classDetails) => classDetails.id === classId) ?? null;
			this.currentClass = classToSet;
			return this.currentClass;
		},

		clearCurrentClass: function () {
			this.currentClass = null;
		},

		setStudentsInClass: function (classStudents: StudentInfo[]): StudentInfo[] {
			this.studentsInClass = classStudents;
			return this.studentsInClass;
		},

		fetchStudentsInCurrentClass: async function (): Promise<StudentInfo[]> {
			this.studentsInClass = (
				(await apiClientProvider().studentsInClass(this.currentClass!.id)).data as StudentsInClassResponse
			).students;
			return this.studentsInClass;
		},

		fetchCategories: async function (applicableStatus?: ProjectStatus[]): Promise<CategoryTypeV2[]> {
			const status: ProjectStatus[] = applicableStatus ?? ['ACTIVE', 'UPCOMING', 'INACTIVE'];
			const apiResponse = (await apiClientProvider().categories(status)).data as { categories: CategoryTypeV2[] };
			this.categories = apiResponse.categories;
			return apiResponse.categories;
		},

		fetchCategoryDetails: async function (id: string): Promise<CategoryDetails> {
			return (await apiClientProvider().categoryDetails(id)).data as CategoryDetails;
		},

		fetchCategoryMetadata: async function (id: string): Promise<CategoryMetadataResponse | HttpErrorResult> {
			return await apiClientProvider().categoryMetadata(id);
		},

		fetchCategoryMetadataMultiple: async (
			categoryIds: string[],
		): Promise<CategoryMetadataResponseMultiple | HttpErrorResult> => {
			return await apiClientProvider().categoryMetadataMultiple(categoryIds);
		},

		fetchNewLessons: async function (categoryId?: string): Promise<CategoryLesson[] | HttpErrorResult> {
			return await apiClientProvider().newLessons(categoryId);
		},

		fetchCategoryLessonsV2: async function (id: string): Promise<LessonInfoV2[]> {
			return ((await apiClientProvider().categoryProjects(id)).data as { projects: LessonInfoV2[] }).projects;
		},

		findEvaluationCriteria: async function (classId: string, lessonId: string): Promise<AssessmentRubricType> {
			return (await apiClientProvider().evaluationCriteria(classId, lessonId)).data as AssessmentRubricType;
		},

		evaluateStudent: async function (submissionId: string, evaluation: EvaluationType[]): Promise<Submission> {
			return (await apiClientProvider().evaluateStudent(submissionId, evaluation)).data as Submission;
		},

		addCommentToSubmission: async function (submissionId: string, content: string): Promise<CommentType[]> {
			return (await apiClientProvider().addComment(submissionId, content)).data as CommentType[];
		},

		requestToResubmit: async function (submissionId: string, comment: string): Promise<Submission> {
			return (await apiClientProvider().requestResubmit(submissionId, comment)).data as Submission;
		},

		addSubmissionToFavorite: async function (submissionId: string): Promise<void> {
			await apiClientProvider().submissionToFavorite(submissionId);
		},

		removeSubmissionFromFavorite: async function (submissionId: string): Promise<void> {
			await apiClientProvider().removeSubFromFavorite(submissionId);
		},

		fetchClassSubmissions: async function (currentClassId: string, currentLessonId: string): Promise<SubmissionInfo[]> {
			return ((await apiClientProvider().submissions(currentClassId, currentLessonId)).data as SubmissionsResponse)
				.submissions;
		},

		fetchSubmissionDetails: async function (submissionId: string): Promise<Submission> {
			return (await apiClientProvider().submissionDetails(submissionId)).data as Submission;
		},

		favoriteSubmissions: async function (): Promise<Submission[]> {
			return ((await apiClientProvider().favoriteSubmissions()).data as { submissions: Submission[] }).submissions;
		},

		importStudentsIntoClass: async function (
			classId: string,
			studentsToImport: AddStudentRecord[],
		): Promise<StudentInfo[] | HttpErrorResult> {
			const importStudentsResponse = await apiClientProvider().importStudentsIntoClass(classId, studentsToImport);

			if (isStudentImportSuccess(importStudentsResponse)) {
				return await this.fetchStudentsInCurrentClass();
			} else {
				return importStudentsResponse;
			}
		},

		deleteStudent: async function (studentId: string): Promise<number> {
			return ((await apiClientProvider().deleteStudent(studentId)).data as SuccessResponse).code;
		},

		updateStudent: async function (userDetails: {
			studentId: string;
			name: string;
			loginCode: string;
		}): Promise<StudentInfo> {
			return (await apiClientProvider().updateStudent(userDetails)).data as StudentInfo;
		},

		fetchShowcases: async function (): Promise<ShowcaseInfo[]> {
			return ((await apiClientProvider().showcases()).data as ShowcasesResponse).showcases;
		},

		fetchShowcase: async function (id: string): Promise<ShowcaseInfo> {
			return (await apiClientProvider().showcase(id)).data as ShowcaseInfo;
		},

		fetchPublicShowcase: async function (id: string): Promise<PublicShowcaseInfo> {
			return (await apiClientProvider().publicShowcase(id)).data as PublicShowcaseInfo;
		},

		createShowcase: async function (showcase: {
			name: string;
			description: string;
			subtitle: string;
			attribution: string;
			submissions: string[];
		}): Promise<ShowcaseInfo> {
			return (await apiClientProvider().createShowcase(showcase)).data as ShowcaseInfo;
		},

		updateShowcase: async function (
			id: string,
			showcase: Partial<{
				name: string;
				description: string;
				subtitle: string;
				attribution: string;
				submissions: string[];
			}>,
		): Promise<ShowcaseInfo> {
			return (await apiClientProvider().updateShowcase(id, showcase)).data as ShowcaseInfo;
		},

		changeShowcaseMode: async function (data: ChangeShowcaseMode): Promise<ChangeShowcaseMode> {
			return (await apiClientProvider().changeShowcaseMode(data)).data as ChangeShowcaseMode;
		},

		deleteShowcase: async function (id: string): Promise<void> {
			await apiClientProvider().deleteShowcase(id);
		},

		fetchLeaderboard: async function (lessonId: string, classId: string) {
			return (await apiClientProvider().fetchLeaderboard(lessonId, classId)).data as LeaderboardResponse;
		},

		resetAllLeaderboardScores: async function (lessonId: string, classId: string): Promise<void> {
			await apiClientProvider().resetAllLeaderboardScores(lessonId, classId);
		},

		resetStudentLeaderboardScore: async function (lessonId: string, classId: string, studentId: string): Promise<void> {
			await apiClientProvider().resetStudentLeaderboardScore(lessonId, classId, studentId);
		},

		fetchAssignmentsForClass: async function (classId: string): Promise<ClassAssignmentsResponse> {
			return (await apiClientProvider().fetchAssignmentsForClass(classId)).data as ClassAssignmentsResponse;
		},
	},
});

const isStudentImportSuccess = (
	response: StudentClassImportResponse | HttpErrorResult,
): response is StudentClassImportResponse => {
	return 'classId' in response;
};

export { LoginResponse, LoginFailure, LoginError };
export {
	type UserDetails,
	type UserCredentials,
	type UserState,
	useUserStore,
	UserType,
	type DetailedClassInfo,
	type StudentInfo,
	type ProjectInfo,
	type LessonInfoV2,
	type AssessmentRubricType,
	type SubmissionInfo,
	type Submission,
	type ShowcaseInfo,
	type PublicShowcaseInfo,
	type ChangeShowcaseMode,
	type ProjectWithClassesRes,
	type ClassAssignment,
	type CategoriesType,
	ProjectPricingTier,
	type NewClassType,
	type CategoryDetails,
	type LeaderboardScore,
	type LeaderboardResponse,
};
